// Host-owned settings persistence (ADR-0014).
//
// Two halves behind one `SettingsApi`:
//
// - LIVE, Playbook BE-owned personal preferences with compare-and-swap
//   versions and audited writes: the presentation preference (theme, locale,
//   time zone) and the launcher preference (favourites, show-more, default
//   landing). Both go over the BFF cookie session to
//   `/api/v1/platform/preferences/*`.
// - MOCK, in memory: Player AI general and Notifications, partitioned exactly
//   the way a live endpoint would scope them — personal prefs by
//   `tenantId::userId`, workspace config by `tenantId` (CLAUDE.md rule 3) —
//   until PB-5136 / PB-5137 give them a backend. A reload loses them.
//
// Scope is read internally from a claims-getter (default: the shell auth store),
// never passed by callers — mirroring how the live client derives scope from the
// session cookie server-side. Tests inject a stub getter for isolation coverage.

import { PLATFORM_THEMES } from "@playbookai/types";
import { useAuthStore } from "../auth/auth-store.js";
import { csrfHeaders } from "../auth/session-api.js";
import { SEED_PERSONAL, SEED_WORKSPACE } from "../settings/seed.js";
import type {
  LauncherPrefs,
  PersonalSettings,
  PresentationPrefs,
  ReplaceLauncherPrefs,
  ReplacePresentationPrefs,
  SettingsData,
  SettingsPatch,
  WorkspacePrefs,
} from "../settings/types.js";

export interface SettingsScope {
  readonly tenantId: string;
  readonly userId: string;
}

export interface SettingsApi {
  /** Durable Playbook BE-owned personal presentation state. */
  getPresentationPreferences(): Promise<PresentationPrefs>;
  /** Full compare-and-swap replacement of the caller's presentation state. */
  replacePresentationPreferences(
    input: ReplacePresentationPrefs,
  ): Promise<PresentationPrefs>;
  /** Durable Playbook BE-owned launcher preference (own CAS version). */
  getLauncherPreferences(): Promise<LauncherPrefs>;
  /** Full compare-and-swap replacement of the caller's launcher preference. */
  replaceLauncherPreferences(
    input: ReplaceLauncherPrefs,
  ): Promise<LauncherPrefs>;
  /** Read the caller's mock-backed settings (workspace = tenant-scoped, rest = user-scoped). */
  getSettings(): Promise<SettingsData>;
  /** Merge a coarse patch of any of the mock-backed sections; returns the result. */
  updateSettings(patch: SettingsPatch): Promise<SettingsData>;
}

export interface CreateSettingsApiDeps {
  /** Resolve the active scope. Returns null when unauthenticated. */
  readonly getScope?: () => SettingsScope | null;
  /** Test seam for the durable presentation owner; production uses fetch. */
  readonly requestPresentation?: (
    method: "GET" | "PUT",
    body?: ReplacePresentationPrefs,
  ) => Promise<PresentationPrefs>;
  /** Test seam for the durable launcher owner; production uses fetch. */
  readonly requestLauncher?: (
    method: "GET" | "PUT",
    body?: ReplaceLauncherPrefs,
  ) => Promise<LauncherPrefs>;
}

/**
 * A durable preference write lost its compare-and-swap race: another tab,
 * device or the Player AI action path saved first. Callers refetch and let the
 * user re-apply; `actualVersion` is the authoritative version to retry against.
 */
export class PreferenceConflictError extends Error {
  readonly code = "PLATFORM_PREFERENCE_VERSION_CONFLICT" as const;
  constructor(
    message: string,
    readonly expectedVersion: number,
    readonly actualVersion: number,
  ) {
    super(message);
    this.name = "PreferenceConflictError";
  }
}

export function isPreferenceConflict(
  error: unknown,
): error is PreferenceConflictError {
  return error instanceof PreferenceConflictError;
}

function defaultGetScope(): SettingsScope | null {
  const claims = useAuthStore.getState().claims;
  // No scope until a tenant is known — callers gate on this (requireScope
  // throws). Avoids inventing a tenant id (CLAUDE.md: never hardcode tenant
  // ids). In practice RequireAuth guarantees a tenant before any settings page
  // mounts.
  if (!claims?.tenantId) return null;
  return { tenantId: claims.tenantId, userId: claims.userId };
}

const clone = <T>(value: T): T => structuredClone(value);

/** A `SettingsApi` with an attached `reset()` for the mock partitions. */
export interface MockSettingsApi extends SettingsApi {
  /** Clear the mock partitions — call between tests (the store is a module singleton). */
  reset(): void;
}

export function createSettingsApi(
  deps: CreateSettingsApiDeps = {},
): MockSettingsApi {
  const getScope = deps.getScope ?? defaultGetScope;
  const requestPresentation =
    deps.requestPresentation ?? requestDurablePresentationPreferences;
  const requestLauncher =
    deps.requestLauncher ?? requestDurableLauncherPreferences;

  // Personal prefs keyed by `${tenantId}::${userId}`; workspace by `tenantId`.
  const personalStore = new Map<string, PersonalSettings>();
  const workspaceStore = new Map<string, WorkspacePrefs>();

  function requireScope(): SettingsScope {
    const scope = getScope();
    if (!scope) throw new Error("settingsApi: no authenticated scope");
    return scope;
  }

  function readPersonal(scope: SettingsScope): PersonalSettings {
    const key = `${scope.tenantId}::${scope.userId}`;
    const existing = personalStore.get(key);
    if (existing) return existing;
    const seeded = clone(SEED_PERSONAL);
    personalStore.set(key, seeded);
    return seeded;
  }

  function readWorkspace(scope: SettingsScope): WorkspacePrefs {
    const existing = workspaceStore.get(scope.tenantId);
    if (existing) return existing;
    const seeded = clone(SEED_WORKSPACE);
    workspaceStore.set(scope.tenantId, seeded);
    return seeded;
  }

  return {
    async getPresentationPreferences(): Promise<PresentationPrefs> {
      requireScope();
      return clone(await requestPresentation("GET"));
    },

    async replacePresentationPreferences(
      input: ReplacePresentationPrefs,
    ): Promise<PresentationPrefs> {
      requireScope();
      return clone(await requestPresentation("PUT", input));
    },

    async getLauncherPreferences(): Promise<LauncherPrefs> {
      requireScope();
      return clone(await requestLauncher("GET"));
    },

    async replaceLauncherPreferences(
      input: ReplaceLauncherPrefs,
    ): Promise<LauncherPrefs> {
      requireScope();
      return clone(await requestLauncher("PUT", input));
    },

    async getSettings(): Promise<SettingsData> {
      const scope = requireScope();
      const personal = readPersonal(scope);
      const workspace = readWorkspace(scope);
      return clone({ ...personal, workspace });
    },

    async updateSettings(patch: SettingsPatch): Promise<SettingsData> {
      const scope = requireScope();
      const personal = readPersonal(scope);
      const workspace = readWorkspace(scope);

      // Coarse top-level merge: each patched section replaces the prior one.
      const nextPersonal: PersonalSettings = {
        playerai: patch.playerai ?? personal.playerai,
        notifications: patch.notifications ?? personal.notifications,
      };
      const nextWorkspace: WorkspacePrefs = patch.workspace ?? workspace;

      personalStore.set(
        `${scope.tenantId}::${scope.userId}`,
        clone(nextPersonal),
      );
      workspaceStore.set(scope.tenantId, clone(nextWorkspace));

      return clone({ ...nextPersonal, workspace: nextWorkspace });
    },

    reset(): void {
      personalStore.clear();
      workspaceStore.clear();
    },
  };
}

/** App singleton — reads scope from the shell auth store. */
export const settingsApi: MockSettingsApi = createSettingsApi();

/** Test helper: clear the singleton's mock partitions between cases. */
export function resetSettingsStore(): void {
  settingsApi.reset();
}

const PRESENTATION_PREFERENCES_PATH =
  "/api/v1/platform/preferences/presentation";
const LAUNCHER_PREFERENCES_PATH = "/api/v1/platform/preferences/launcher";

function requestDurablePresentationPreferences(
  method: "GET" | "PUT",
  body?: ReplacePresentationPrefs,
): Promise<PresentationPrefs> {
  return requestDurablePreferences(
    PRESENTATION_PREFERENCES_PATH,
    "Presentation preferences",
    isPresentationPreferences,
    method,
    body,
  );
}

function requestDurableLauncherPreferences(
  method: "GET" | "PUT",
  body?: ReplaceLauncherPrefs,
): Promise<LauncherPrefs> {
  return requestDurablePreferences(
    LAUNCHER_PREFERENCES_PATH,
    "Launcher preferences",
    isLauncherPreferences,
    method,
    body,
  );
}

/**
 * One transport for both durable owners: same-origin cookie session, CSRF on
 * writes, and a 409 surfaced as `PreferenceConflictError` (code plus the
 * authoritative version) instead of a bare message, so a page can say
 * "updated elsewhere" and retry against `actualVersion`.
 */
async function requestDurablePreferences<T>(
  path: string,
  label: string,
  guard: (value: unknown) => value is T,
  method: "GET" | "PUT",
  body?: unknown,
): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(method === "PUT" ? csrfHeaders() : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    const problem = (await response.json().catch(() => null)) as {
      message?: string;
      code?: string;
      details?: { expectedVersion?: unknown; actualVersion?: unknown };
    } | null;
    if (
      response.status === 409 &&
      problem?.code === "PLATFORM_PREFERENCE_VERSION_CONFLICT" &&
      typeof problem.details?.actualVersion === "number"
    ) {
      throw new PreferenceConflictError(
        problem.message ?? `${label} changed elsewhere.`,
        typeof problem.details.expectedVersion === "number"
          ? problem.details.expectedVersion
          : -1,
        problem.details.actualVersion,
      );
    }
    throw new Error(
      problem?.message ?? `${label} request failed (${response.status}).`,
    );
  }
  const payload: unknown = await response.json().catch(() => null);
  if (!guard(payload)) {
    throw new Error(
      `The server returned ${label.toLowerCase()} we could not read.`,
    );
  }
  return payload;
}

function hasVersionFields(item: Record<string, unknown>): boolean {
  return (
    typeof item["version"] === "number" &&
    Number.isSafeInteger(item["version"]) &&
    item["version"] >= 0 &&
    (item["updatedAt"] === null || typeof item["updatedAt"] === "string")
  );
}

function isPresentationPreferences(value: unknown): value is PresentationPrefs {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item["theme"] === "string" &&
    (PLATFORM_THEMES as readonly string[]).includes(item["theme"]) &&
    typeof item["locale"] === "string" &&
    typeof item["timeZone"] === "string" &&
    hasVersionFields(item)
  );
}

function isLauncherPreferences(value: unknown): value is LauncherPrefs {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const item = value as Record<string, unknown>;
  return (
    Array.isArray(item["favourites"]) &&
    item["favourites"].every((id) => typeof id === "string") &&
    typeof item["moduleRosterConfigured"] === "boolean" &&
    typeof item["showMoreApps"] === "boolean" &&
    typeof item["defaultLanding"] === "string" &&
    hasVersionFields(item)
  );
}
